import PropTypes from "prop-types";
import React from "react";

import { getAutomationDataAttr } from "@/utils/automationUtils";

import { Card } from "../Card";
import { createClassNameString, insuranceCompanyFilterOptions } from "../utils";

import { CompletedProfileInsurance } from "./CompletedProfileInsurance";
import "./styles.scss";
import { EditProfileInsurance } from "./EditProfileInsurance";
import { EmptyProfileInsurance } from "./EmptyProfileInsurance";

export function MemberProfileInsurance({
  className,
  completedTitle,
  editTitle,
  emptyDetails,
  emptyTitle,
  errors,
  files,
  groupId,
  groupIdTooltipContents,
  handleBlur,
  handleChange,
  handleFocus,
  insuranceCardImgBackSrc,
  insuranceCardImgBackStatus,
  insuranceCardImgFrontSrc,
  insuranceCardImgFrontStatus,
  insuranceCompaniesOptions,
  insuranceCompany,
  isAso,
  isEditing,
  isEmpty,
  isPrimaryInsurance,
  memberId,
  memberIdPlaceholder,
  memberIdTip,
  memberIdTooltipContents,
  noInsurance,
  onAddButtonClick,
  onBrowseButtonClicked,
  onCancelButtonClick,
  onEditButtonClick,
  onFileDelete,
  onInsuranceCardImgBackClick,
  onInsuranceCardImgFrontClick,
  onSubmitButtonClick,
  policyHolder,
  policyHolderDOB,
  policyHolderName,
  policyHolderOptions,
  stateOfInsurancePlan,
  stateOfInsurancePlanOptions,
  touched,
  values,
  ...props
}) {
  return (
    <Card
      className={createClassNameString(["apti-MemberProfileInsurance", className])}
      {...props}
      {...getAutomationDataAttr(
        `patient-chart-insurance-${isPrimaryInsurance ? "primary" : "secondary"}`,
      )}
    >
      {isEmpty && !isEditing && (
        <EmptyProfileInsurance
          details={emptyDetails}
          onAddButtonClick={onAddButtonClick}
          title={emptyTitle}
        />
      )}

      {isEditing && (
        <EditProfileInsurance
          errors={errors}
          files={files}
          groupIdTooltipContents={groupIdTooltipContents}
          handleBlur={handleBlur}
          handleChange={handleChange}
          handleFocus={handleFocus}
          insuranceCompaniesOptions={insuranceCompaniesOptions}
          insuranceCompanyFilterOptions={insuranceCompanyFilterOptions}
          memberIdPlaceholder={memberIdPlaceholder}
          memberIdTip={memberIdTip}
          memberIdTooltipContents={memberIdTooltipContents}
          noInsurance={noInsurance}
          onBrowseButtonClicked={onBrowseButtonClicked}
          onCancelButtonClick={onCancelButtonClick}
          onFileDelete={onFileDelete}
          onSubmitButtonClick={onSubmitButtonClick}
          policyHolderOptions={policyHolderOptions}
          stateOfInsurancePlanOptions={stateOfInsurancePlanOptions}
          title={editTitle}
          touched={touched}
          values={values}
        />
      )}

      {!isEmpty && !isEditing && (
        <CompletedProfileInsurance
          groupId={groupId}
          insuranceCardImgBackSrc={insuranceCardImgBackSrc}
          insuranceCardImgBackStatus={insuranceCardImgBackStatus}
          insuranceCardImgFrontSrc={insuranceCardImgFrontSrc}
          insuranceCardImgFrontStatus={insuranceCardImgFrontStatus}
          insuranceCompany={insuranceCompany}
          isAso={isAso}
          memberId={memberId}
          onEditButtonClick={onEditButtonClick}
          onInsuranceCardImgBackClick={onInsuranceCardImgBackClick}
          onInsuranceCardImgFrontClick={onInsuranceCardImgFrontClick}
          policyHolder={policyHolder}
          policyHolderDOB={policyHolderDOB}
          policyHolderName={policyHolderName}
          stateOfInsurancePlan={stateOfInsurancePlan}
          title={completedTitle}
        />
      )}
    </Card>
  );
}

MemberProfileInsurance.propTypes = {
  className: PropTypes.string,
  // -- Completed Props
  completedTitle: PropTypes.string,
  // -- Edit Props
  editTitle: PropTypes.string,
  emptyDetails: PropTypes.string,
  // -- Empty Props
  emptyTitle: PropTypes.string,

  errors: PropTypes.object,
  files: PropTypes.arrayOf(PropTypes.string),

  groupId: PropTypes.string,

  handleBlur: PropTypes.func,
  handleChange: PropTypes.func,
  handleFocus: PropTypes.func,
  insuranceCardImgBackSrc: PropTypes.string,
  insuranceCardImgBackStatus: PropTypes.string,
  insuranceCardImgFrontSrc: PropTypes.string,

  insuranceCardImgFrontStatus: PropTypes.string,
  insuranceCompaniesOptions: PropTypes.arrayOf(PropTypes.object),
  insuranceCompany: PropTypes.string,

  isEditing: PropTypes.bool,
  isEmpty: PropTypes.bool,
  isPrimaryInsurance: PropTypes.bool,
  memberId: PropTypes.string,

  noInsurance: PropTypes.bool,

  onAddButtonClick: PropTypes.func,
  onBrowseButtonClicked: PropTypes.func,
  onCancelButtonClick: PropTypes.func,
  onEditButtonClick: PropTypes.func,
  onFileDelete: PropTypes.func,
  onInsuranceCardImgBackClick: PropTypes.func,
  onInsuranceCardImgFrontClick: PropTypes.func,

  onSubmitButtonClick: PropTypes.func,
  policyHolder: PropTypes.string,

  policyHolderDOB: PropTypes.string,
  policyHolderName: PropTypes.string,

  policyHolderOptions: PropTypes.arrayOf(PropTypes.object),
  stateOfInsurancePlan: PropTypes.string,

  stateOfInsurancePlanOptions: PropTypes.arrayOf(PropTypes.object),
  touched: PropTypes.object,

  // Formik props
  values: PropTypes.object,
};

MemberProfileInsurance.defaultProps = {
  className: undefined,
  // -- Completed Props
  completedTitle: "",
  // -- Edit Props
  editTitle: "",
  emptyDetails: "",
  // -- Empty Props
  emptyTitle: "",

  errors: {},
  files: [],

  groupId: "",

  handleBlur: () => {},
  handleChange: () => {},
  handleFocus: () => {},
  insuranceCardImgBackSrc: "",
  insuranceCardImgBackStatus: "",
  insuranceCardImgFrontSrc: "",

  insuranceCardImgFrontStatus: "",
  insuranceCompaniesOptions: [],
  insuranceCompany: "",

  isEditing: false,
  isEmpty: false,
  memberId: "",

  noInsurance: false,

  onAddButtonClick: () => {},
  onBrowseButtonClicked: () => {},
  onCancelButtonClick: () => {},
  onEditButtonClick: () => {},
  onFileDelete: () => {},
  onInsuranceCardImgBackClick: () => {},
  onInsuranceCardImgFrontClick: () => {},

  onSubmitButtonClick: () => {},
  policyHolder: "",

  policyHolderDOB: "",
  policyHolderName: "",

  policyHolderOptions: [],
  stateOfInsurancePlan: "",

  stateOfInsurancePlanOptions: [],
  touched: {},

  // Formik props
  values: {},
};
